import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of, tap } from 'rxjs';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { environment } from 'environments/environment';
import { APP_FRONT_VERSION } from 'app/core/version/app-version';
import { BackendVersionResponse, FrontendVersionManifest, VersionState } from 'app/core/version/version.types';

const INITIAL_VERSION_STATE: VersionState = {
    frontVersion: APP_FRONT_VERSION,
    latestFrontVersion: APP_FRONT_VERSION,
    backVersion: null,
    isFrontUpToDate: true
};

@Injectable({
    providedIn: 'root'
})
export class AppVersionService {
    private _versionState = new BehaviorSubject<VersionState>(INITIAL_VERSION_STATE);
    private _isUpdateDialogOpen = false;

    constructor(
        private _httpClient: HttpClient,
        private _fuseConfirmationService: FuseConfirmationService
    ) {}

    get versionState$(): Observable<VersionState> {
        return this._versionState.asObservable();
    }

    refreshVersionState(): Observable<VersionState> {
        return forkJoin({
            latestFrontVersion: this._getLatestFrontVersion(),
            backVersion: this._getBackVersion(),
        }).pipe(
            map(({ latestFrontVersion, backVersion }) => ({
                frontVersion: APP_FRONT_VERSION,
                latestFrontVersion,
                backVersion,
                isFrontUpToDate: latestFrontVersion === APP_FRONT_VERSION
            })),
            tap((versionState) => {
                this._versionState.next(versionState);

                if (!versionState.isFrontUpToDate) {
                    this._openFrontVersionMismatchDialog(versionState);
                }
            })
        );
    }

    private _getLatestFrontVersion(): Observable<string> {
        return this._httpClient
            .get<FrontendVersionManifest>(`assets/app-version.json?ts=${Date.now()}`)
            .pipe(
                map((response) => response.version || APP_FRONT_VERSION),
                catchError(() => of(APP_FRONT_VERSION))
            );
    }

    private _getBackVersion(): Observable<string | null> {
        return this._httpClient
            .get<BackendVersionResponse>(`${environment.serverUrl}/version`)
            .pipe(
                map((response) => response.version || null),
                catchError(() => of(null))
            );
    }

    private _openFrontVersionMismatchDialog(versionState: VersionState): void {
        if (this._isUpdateDialogOpen) {
            return;
        }

        this._isUpdateDialogOpen = true;

        const backVersionMessage = versionState.backVersion
            ? `<br><br>Versión actual del backend: <strong>${versionState.backVersion}</strong>.`
            : '';

        const dialogRef = this._fuseConfirmationService.open({
            title: 'Hay una nueva versión disponible',
            message: `Esta ventana tiene cargada la versión <strong>${versionState.frontVersion}</strong> y la última publicada es la <strong>${versionState.latestFrontVersion}</strong>.<br><br>Pulsa "Actualizar ahora" para recargar la aplicación.${backVersionMessage}`,
            icon: {
                show: true,
                name: 'heroicons_outline:refresh',
                color: 'warning'
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Actualizar ahora',
                    color: 'primary'
                },
                cancel: {
                    show: false,
                    label: 'Cancelar'
                }
            },
            dismissible: false
        });

        dialogRef.afterClosed().subscribe((result) => {
            this._isUpdateDialogOpen = false;

            if (result === 'confirmed') {
                window.location.reload();
            }
        });
    }
}
